import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Injector, Optional, inject } from '@angular/core';
import {
  catchError,
  map,
  Observable,
  of,
  firstValueFrom,
  from,
  switchMap,
  tap,
  defer,
  finalize,
} from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import {
  GlobalServicesLoginDetails,
  OAuth2RedirectUri,
  OAuth2Token,
} from '@maximizer/core/shared/domain';
import {
  GLOBAL_SERVICES_KEY,
  GLOBAL_SERVICES_URL,
  OAUTH2_CLIENT_ID,
  OAUTH2_CLIENT_ID_EMEA,
  ContextService,
  OnPremLoginDetails,
} from '@maximizer/core/shared/data-access';
import { InsightsService } from '@maximizer/core/shared/insights';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { EnhancedEncryptionService } from './enhanced-encryption.service';
import { SecureStorageService } from './secure-storage.service';
import {
  OAUTH2_PEER_TOKEN_BRIDGE,
  OAuth2PeerTokenBridge,
} from './peer-token-bridge.port';
import { IS_BROWSER_ONLY_ROUTE } from '../tokens/browser-only-route.token';
import { hashForTracking } from '../utils/hash-tracking.util';

import {
  RegionalSubdomain,
  BrowserOnlyAccessTokenHydration,
  StoredUserCredentials,
  StoredLoginDetails,
} from '../models/oauth2';
import {
  deobfuscateStorageValue,
  normalizeError,
  obfuscateStorageValue,
} from '@maximizer/shared/util';
import {
  CODE_CHALLENGE_METHOD,
  DATABASE_LOGIN_HINT_PREFIX,
  AUTH_CLEARED_MARKER_KEY,
  ENHANCED_STORAGE_KEYS,
  AUTH_ERROR_SESSION_KEY,
  FIRST_WORKSPACE_INDEX,
  GRANT_TYPES,
  HTTP_PARAMS,
  DEFAULT_BROWSER_ONLY_EXPIRES_IN,
  HTTP_STATUS,
  IS_CLOUD_STORAGE_KEY,
  MILLISECONDS_PER_SECOND,
  OAUTH2_PATHS,
  OAUTH2_RESPONSE_TYPE,
  SCOPE,
  STORAGE_KEY_PARTITION,
  STORAGE_NAME_LAST_USE,
  STORAGE_NAME_LOGIN_DETAILS,
  STORAGE_NAME_ON_PREM_HOST,
  STORAGE_NAME_REFRESH_IN_PROGRESS,
  STORAGE_NAME_TOKEN,
  STORAGE_NAME_USER_EMAIL,
  STORAGE_NAME_USER_ID,
  TOKEN_BUFFER_MS,
  URL_PARAMS,
  URL_PREFIX_CHECK_LENGTH,
} from '../models/constants';
import { isTokenEffectivelyValid } from '../utils/token-validity.util';

@Injectable()
export class OAuth2Service {
  private readonly injector = inject(Injector);
  private readonly isBrowserOnlyRoute = inject(IS_BROWSER_ONLY_ROUTE);
  private insightsService: InsightsService | null | undefined = undefined;
  private readonly AUTH_CLEARED_MARKER_TTL_MS = 30_000;
  private readonly REFRESH_CORRUPTION_GRACE_WINDOW_MS = 30_000;
  private readonly REUSE_WAIT_RETRY_DELAYS_MS = [300, 700, 1500];
  private readonly LAST_USE_WRITE_THROTTLE_MS = 20_000;

  private lastAuthenticatedUseMs = 0;
  private lastPersistedUseMs = 0;
  private lastUsePersistFailureTracked = false;

  private clearingInProgress = false;
  private refreshPromise?: Promise<OAuth2Token | null>;
  private lastUsedRefreshTokenHash?: string;
  private readonly refreshTokenUsageCount = new Map<string, number>();
  private lastRefreshFailureReason?: string;

  oAuth2Token: OAuth2Token | undefined;
  cloudLoginDetails?: GlobalServicesLoginDetails;
  onPremLoginDetails?: OnPremLoginDetails;
  encryptPassword?: string;
  storagePartitionKey = '';
  mxUserId?: string;
  mxUserEmail?: string;

  get mxWorkspaceId(): string {
    return (
      this.cloudLoginDetails?.tenant.workspaces[FIRST_WORKSPACE_INDEX].id ?? ''
    );
  }

  get requiredPropertiesPopulated(): boolean {
    return !!(
      this.mxUserId &&
      this.oAuth2Token &&
      this.mxUserEmail &&
      this.requiredLoginDetailsPopulated
    );
  }

  get requiredLoginDetailsPopulated(): boolean {
    const isCloud = this.getIsCloudFromStorage();

    if (isCloud) {
      return !!this.cloudLoginDetails;
    } else {
      return !!this.onPremLoginDetails;
    }
  }

  getLastRefreshFailureReason(): string | undefined {
    return this.lastRefreshFailureReason;
  }

  isRefreshActive(): boolean {
    return !!this.refreshPromise;
  }

  waitForActiveRefresh(timeoutMs: number): Promise<OAuth2Token | null> {
    if (!this.refreshPromise) {
      return Promise.resolve(null);
    }

    return this.withTimeout(this.refreshPromise, timeoutMs);
  }

  async syncTokenFromStorage(options?: {
    skipRefresh?: boolean;
  }): Promise<OAuth2Token | undefined> {
    const token = await this.getStorageToken(options);
    if (!token) {
      return undefined;
    }

    return this.adoptTokenState(token);
  }

  constructor(
    @Inject(GLOBAL_SERVICES_KEY) private readonly globalServicesKey: string,
    @Inject(GLOBAL_SERVICES_URL) private readonly globalServicesUrl: string,
    @Inject(OAUTH2_CLIENT_ID) private readonly clientId: string,
    @Inject(OAUTH2_CLIENT_ID_EMEA) private readonly clientIdEmea: string,
    private readonly http: HttpClient,
    private readonly encryption: EnhancedEncryptionService,
    private readonly context: ContextService,
    private readonly secureStorage: SecureStorageService,
    @Optional()
    @Inject(OAUTH2_PEER_TOKEN_BRIDGE)
    private readonly peerTokenBridge: OAuth2PeerTokenBridge | null = null,
  ) {}

  private getInsightsService(): InsightsService | null {
    if (this.insightsService !== undefined) {
      return this.insightsService;
    }

    this.insightsService = this.injector.get(InsightsService, null);
    return this.insightsService;
  }

  getLoginDetails(
    email: string,
  ): Observable<GlobalServicesLoginDetails[] | null> {
    const url = `${this.globalServicesUrl}/v1/users/${encodeURIComponent(email)}/loginDetails`;
    const headers = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.globalServicesKey,
    });

    return this.http.get<GlobalServicesLoginDetails[]>(url, { headers }).pipe(
      map((result) => result ?? []),
      catchError((error) => {
        this.trackAuthException(
          'oauth2-service-login-details-fetch-failed',
          error,
          {
            status: error?.status,
          },
        );
        return of(null);
      }),
    );
  }

  getIsCloudFromStorage(): boolean {
    return localStorage.getItem(IS_CLOUD_STORAGE_KEY) !== 'false';
  }

  getIsCloudFromStorageWithFallback(): boolean | null {
    const isCloudValue = localStorage.getItem(IS_CLOUD_STORAGE_KEY);
    if (isCloudValue === null) {
      return null;
    }
    return isCloudValue !== 'false';
  }

  getToken(
    code: string,
    redirect_uri: string,
    verifier: string,
    login_hint: string,
    storeLocal = true,
    isCloud = true,
  ): Observable<OAuth2Token | null> {
    const validation = this.validateOAuth2Params({
      code,
      redirect_uri,
      state: verifier,
    });

    if (!validation.valid) {
      this.trackAuthException(
        'oauth2-service-params-invalid',
        new Error('Invalid OAuth2 parameters'),
      );
      return of(null);
    }

    const sanitizedCode = validation.sanitized.code ?? code;
    const sanitizedRedirectUri =
      validation.sanitized.redirect_uri ?? redirect_uri;
    const sanitizedVerifier = validation.sanitized.state ?? verifier;

    if (!sanitizedCode || !sanitizedRedirectUri || !sanitizedVerifier) {
      this.trackAuthException(
        'oauth2-service-params-missing-after-sanitization',
        new Error('Missing required OAuth2 parameters after sanitization'),
      );
      return of(null);
    }

    let oauthUrl: string | null = null;
    if (isCloud) {
      oauthUrl = this.getOAuthUrl();
    } else {
      oauthUrl = this.onPremLoginDetails?.host ?? null;
    }

    if (!oauthUrl) return of(null);

    let clientId = '';
    let url = '';
    if (isCloud) {
      clientId = this.getRegionClientId(oauthUrl);
      url = `${oauthUrl}${OAUTH2_PATHS.TOKEN}`;
    } else {
      clientId = this.onPremLoginDetails?.clientId ?? '';
      url = `${oauthUrl}${OAUTH2_PATHS.ON_PREM_TOKEN}`;
    }

    const body = new HttpParams()
      .set(HTTP_PARAMS.GRANT_TYPE, GRANT_TYPES.AUTHORIZATION_CODE)
      .set(HTTP_PARAMS.CLIENT_ID, clientId)
      .set(HTTP_PARAMS.REDIRECT_URI, sanitizedRedirectUri)
      .set(HTTP_PARAMS.CODE, sanitizedCode)
      .set(HTTP_PARAMS.CODE_VERIFIER, sanitizedVerifier)
      .set(HTTP_PARAMS.LOGIN_HINT, login_hint);

    return this.requestToken(url, body, storeLocal);
  }

  refreshToken(
    refresh_token: string | undefined = undefined,
    storeLocal = true,
    callerTag = 'missing-caller-tag',
  ): Observable<OAuth2Token | null> {
    return defer(() => {
      if (this.refreshPromise) {
        this.trackAuthTrace(
          'oauth2-service-token-refresh-reuse-existing-promise',
          {
            storeLocal,
            callerTag,
          },
        );
        return from(this.refreshPromise);
      }

      this.trackAuthTrace('oauth2-service-token-refresh-start', {
        storeLocal,
        callerTag,
      });
      this.lastRefreshFailureReason = undefined;

      this.refreshPromise = firstValueFrom(
        this.performRefresh(refresh_token, storeLocal),
        { defaultValue: null },
      ).finally(() => {
        this.trackAuthTrace('oauth2-service-token-refresh-completed', {
          storeLocal,
          callerTag,
        });
        this.refreshPromise = undefined;
      });

      return from(this.refreshPromise);
    });
  }

  private performRefresh(
    refresh_token: string | undefined,
    storeLocal: boolean,
  ): Observable<OAuth2Token | null> {
    return from(this.getStorageToken({ skipRefresh: true })).pipe(
      switchMap((storageToken) => {
        if (storageToken) {
          this.adoptTokenState(storageToken);
        }

        const currentRefreshToken =
          refresh_token ??
          storageToken?.refresh_token ??
          this.oAuth2Token?.refresh_token;

        if (!currentRefreshToken) {
          this.trackAuthException(
            'oauth2-service-token-refresh-missing-refresh-token',
            new Error('Missing refresh token'),
          );
          return of(null);
        }

        const tokenHash = hashForTracking(currentRefreshToken);
        const usageCount =
          (this.refreshTokenUsageCount.get(tokenHash) ?? 0) + 1;
        this.refreshTokenUsageCount.set(tokenHash, usageCount);
        const isTokenReuse = usageCount > 1;
        const wasPreviouslyUsed = this.lastUsedRefreshTokenHash === tokenHash;

        if (isTokenReuse) {
          this.trackAuthTrace(
            'oauth2-service-token-refresh-reuse-blocked',
            {
              tokenHash,
              usageCount,
              wasPreviouslyUsed,
              lastUsedTokenHash: this.lastUsedRefreshTokenHash,
            },
            SeverityLevel.Warning,
          );

          return from(this.waitForNewerTokenAfterReuse(tokenHash));
        }

        const peerAttempt = this.peerTokenBridge
          ? from(this.peerTokenBridge.tryGetTokenFromPeer(tokenHash))
          : of<OAuth2Token | null>(null);

        return peerAttempt.pipe(
          switchMap((peerToken) => {
            if (peerToken) {
              this.trackAuthTrace('oauth2-service-token-refresh-delegated', {
                tokenHash,
              });
              return from(
                this.setStorageToken(peerToken, { origin: 'peer' }),
              ).pipe(map(() => peerToken));
            }
            return this.performHttpRefresh(
              currentRefreshToken,
              storeLocal,
              tokenHash,
            );
          }),
        );
      }),
    );
  }

  private performHttpRefresh(
    currentRefreshToken: string,
    storeLocal: boolean,
    tokenHash: string,
  ): Observable<OAuth2Token | null> {
    return defer(() => {
      const isCloud = this.getIsCloudFromStorage();

      if (!this.hasRequiredRefreshLoginDetails(isCloud)) {
        return of(null);
      }

      const { url, clientId } = isCloud
        ? this.getCloudOAuthDetails()
        : this.getOnPremOAuthDetails();

      if (!url) {
        this.trackAuthException(
          'oauth2-service-token-refresh-missing-oauth-url',
          new Error('Missing OAuth URL'),
          { isCloud },
        );
        return of(null);
      }

      const loginHint = this.getLoginHint(isCloud);
      if (!loginHint) {
        this.trackAuthException(
          'oauth2-service-token-refresh-missing-login-hint',
          new Error('Missing login hint'),
          { isCloud },
        );
        return of(null);
      }

      this.trackAuthTrace('oauth2-service-token-refresh-request-sent', {
        url,
        isCloud,
        clientId,
      });

      const body = new HttpParams()
        .set(HTTP_PARAMS.GRANT_TYPE, GRANT_TYPES.REFRESH_TOKEN)
        .set(HTTP_PARAMS.CLIENT_ID, clientId)
        .set(HTTP_PARAMS.REFRESH_TOKEN, currentRefreshToken)
        .set(HTTP_PARAMS.LOGIN_HINT, loginHint);

      this.setRefreshInProgress();
      return this.requestToken(url, body, storeLocal).pipe(
        tap((result) => {
          if (!result) {
            this.refreshTokenUsageCount.delete(tokenHash);
          }
        }),
      );
    });
  }

  private getCloudOAuthDetails(): { url: string | null; clientId: string } {
    const oauthUrl = this.getOAuthUrl();
    const clientId = oauthUrl ? this.getRegionClientId(oauthUrl) : '';
    return {
      url: oauthUrl ? `${oauthUrl}${OAUTH2_PATHS.TOKEN}` : null,
      clientId,
    };
  }

  private getOnPremOAuthDetails(): { url: string | null; clientId: string } {
    const loginDetails = this.onPremLoginDetails;
    const oauthUrl = loginDetails?.host ?? null;
    const clientId = loginDetails?.clientId ?? '';
    return {
      url: oauthUrl ? `${oauthUrl}${OAUTH2_PATHS.ON_PREM_TOKEN}` : null,
      clientId,
    };
  }

  private hasRequiredRefreshLoginDetails(isCloud: boolean): boolean {
    const currentLoginDetails = isCloud
      ? this.cloudLoginDetails
      : this.onPremLoginDetails;
    if (currentLoginDetails) {
      return true;
    }

    const details = isCloud
      ? this.getStorageLoginDetails()
      : this.getStorageOnPremLoginDetails();
    if (details) {
      return true;
    }

    this.trackAuthException(
      isCloud
        ? 'oauth2-service-token-refresh-missing-cloud-login-details'
        : 'oauth2-service-token-refresh-missing-onprem-login-details',
      new Error(
        isCloud
          ? 'Missing cloud login details'
          : 'Missing on-premise login details',
      ),
      { isCloud },
    );
    return false;
  }

  private requestToken(
    url: string,
    body: HttpParams,
    storeLocal: boolean,
  ): Observable<OAuth2Token | null> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const requestStartTime = Date.now();

    const requestRefreshToken =
      body.get(HTTP_PARAMS.REFRESH_TOKEN) ?? undefined;
    const requestTokenHash = requestRefreshToken
      ? hashForTracking(requestRefreshToken)
      : undefined;

    return this.http.post<OAuth2Token>(url, body, { headers }).pipe(
      catchError(async (error) => {
        const requestDuration = Date.now() - requestStartTime;
        const isAuthenticationLost = this.detectAuthenticationLoss(error);
        const errorType = this.categorizeAuthError(error);
        this.lastRefreshFailureReason = errorType;

        this.trackAuthException(
          'oauth2-service-token-request-backend-failed',
          error,
          {
            status: error?.status,
            errorType,
            requestDuration,
            isAuthenticationLost,
          },
        );

        if (errorType === 'INVALID_REFRESH_TOKEN') {
          this.trackAuthTrace(
            'oauth2-service-token-request-recoverable-refresh-failure',
            {
              status: error?.status,
              errorType,
            },
          );
          this.setAuthErrorFlag();
          await this.clearAuthenticationState('invalid-refresh-token-rejected');
          return null;
        }

        if (errorType === 'NETWORK_ERROR_TOKEN_EXPIRED') {
          this.trackAuthException(
            'oauth2-service-token-request-cors-masked-auth-failure',
            error,
            {
              status: error?.status,
              errorType,
              requestDuration,
              authCleared: false,
            },
          );
          return null;
        }

        if (
          requestTokenHash &&
          this.hasDifferentRefreshTokenInMemory(requestTokenHash)
        ) {
          const inMemoryToken = this.oAuth2Token ?? null;
          this.trackAuthTrace(
            'oauth2-service-token-request-concurrent-refresh-detected',
            {
              requestTokenHash,
              currentTokenHash: inMemoryToken?.refresh_token
                ? hashForTracking(inMemoryToken.refresh_token)
                : null,
            },
          );
          return inMemoryToken;
        }

        if (isAuthenticationLost) {
          this.trackAuthException(
            'oauth2-service-token-request-authentication-lost',
            error,
            {
              status: error?.status,
              errorType,
              requestDuration,
            },
          );

          this.setAuthErrorFlag();
          await this.clearAuthenticationState('token-request-auth-loss');
          return null;
        }

        this.trackAuthTrace('oauth2-service-token-request-unhandled-error', {
          status: error?.status,
          errorType,
          requestDuration,
        });
        return null;
      }),
      switchMap(async (result) => {
        if (result) {
          this.lastRefreshFailureReason = undefined;
          const adoptedToken = this.adoptTokenState({
            ...result,
            expires_at: new Date(
              Date.now() + result.expires_in * MILLISECONDS_PER_SECOND,
            ),
          });

          const newTokenHash = hashForTracking(adoptedToken.refresh_token);
          const previousTokenHash = this.lastUsedRefreshTokenHash;
          const tokenChanged = previousTokenHash !== newTokenHash;

          this.recordAuthenticatedUse();

          if (storeLocal) {
            await this.setStorageToken(adoptedToken);
          }

          if (previousTokenHash && previousTokenHash !== newTokenHash) {
            this.refreshTokenUsageCount.delete(previousTokenHash);
          }
          this.lastUsedRefreshTokenHash = newTokenHash;
          this.refreshTokenUsageCount.set(newTokenHash, 0);

          this.trackAuthTrace('oauth2-service-token-refresh-success', {
            newTokenHash,
            tokenChanged,
            expiresIn: adoptedToken.expires_in,
            expiresAt: adoptedToken.expires_at.toISOString(),
            storeLocal,
          });

          return adoptedToken;
        }

        const recoveredToken = await this.getStorageToken({
          skipRefresh: true,
        });
        if (recoveredToken) {
          this.adoptTokenState(recoveredToken);
        }

        if (
          requestTokenHash &&
          this.hasDifferentRefreshTokenInMemory(requestTokenHash) &&
          this.oAuth2Token
        ) {
          this.trackAuthTrace(
            'oauth2-service-token-request-returning-concurrent-token',
            {
              requestTokenHash,
              currentTokenHash: hashForTracking(this.oAuth2Token.refresh_token),
            },
          );
          return this.oAuth2Token;
        }

        if (
          this.oAuth2Token &&
          this.context.refresh_token !== this.oAuth2Token.refresh_token
        ) {
          return this.oAuth2Token;
        }

        return null;
      }),
      finalize(() => {
        this.clearRefreshInProgress();
      }),
    );
  }

  getOAuthLoginUrl(
    cloudLoginDetails: GlobalServicesLoginDetails,
    redirect_uri: string,
  ): OAuth2RedirectUri | null {
    this.cloudLoginDetails = cloudLoginDetails;
    const state = uuidV4();
    const original_code = uuidV4();
    const code_challenge = this.encryption.oauthHashCodeVerifier(original_code);
    const oauthUrl = this.getOAuthUrl();
    const loginHint = this.getLoginHint();

    if (!oauthUrl || !loginHint) return null;
    const clientId = this.getRegionClientId(oauthUrl);

    let url = `${oauthUrl}${OAUTH2_PATHS.AUTHORIZE}?${URL_PARAMS.RESPONSE_TYPE}=${OAUTH2_RESPONSE_TYPE}`;
    url = url + `&${URL_PARAMS.CLIENT_ID}=` + clientId;
    url =
      url + `&${URL_PARAMS.REDIRECT_URI}=` + encodeURIComponent(redirect_uri);
    url = url + `&${URL_PARAMS.STATE}=` + state;
    url = url + `&${URL_PARAMS.CODE_CHALLENGE}=` + code_challenge;
    url = url + `&${URL_PARAMS.CODE_CHALLENGE_METHOD}=` + CODE_CHALLENGE_METHOD;
    url = url + `&${URL_PARAMS.LOGIN_HINT}=` + encodeURIComponent(loginHint);

    return {
      url,
      state,
      code_challenge,
      original_code,
    };
  }

  getOAuthLoginUrlOnPrem(
    host: string,
    redirect_uri: string,
    clientId: string,
  ): OAuth2RedirectUri | null {
    const state = uuidV4();
    const nonce = uuidV4();
    const original_code = uuidV4();
    const code_challenge = this.encryption.oauthHashCodeVerifier(original_code);

    let url = `${host}${OAUTH2_PATHS.ON_PREM_AUTHORIZE}?${URL_PARAMS.RESPONSE_TYPE}=${OAUTH2_RESPONSE_TYPE}`;
    url += `&${URL_PARAMS.CLIENT_ID}=` + clientId;
    url += `&${URL_PARAMS.REDIRECT_URI}=` + encodeURIComponent(redirect_uri);
    url += `&${URL_PARAMS.STATE}=` + state;
    url += `&${URL_PARAMS.NONCE}=` + nonce;
    url += `&${URL_PARAMS.SCOPE}=` + SCOPE;
    url += `&${URL_PARAMS.CODE_CHALLENGE}=` + code_challenge;
    url += `&${URL_PARAMS.CODE_CHALLENGE_METHOD}=` + CODE_CHALLENGE_METHOD;

    return {
      url,
      state,
      code_challenge,
      original_code,
      nonce,
    };
  }

  async populateFromStorage(): Promise<boolean> {
    try {
      const storageUser = this.getStorageMxUserId();
      const storageUserEmail = this.getStorageMxUserEmail();
      const storageToken = await this.getStorageToken({ skipRefresh: true });

      const isCloudValue = this.getIsCloudFromStorageWithFallback();
      if (isCloudValue === null) {
        return false;
      }

      const isCloud = isCloudValue;
      const storageLogin = isCloud
        ? this.getStorageLoginDetails()
        : this.getStorageOnPremLoginDetails();

      if (
        !this.areRequiredCredentialsValid(
          storageUser,
          storageUserEmail,
          storageLogin,
          storageToken,
        )
      ) {
        return false;
      }

      this.mxUserId = storageUser!;
      this.mxUserEmail = storageUserEmail!;
      this.oAuth2Token = storageToken;

      if (isCloud) {
        this.cloudLoginDetails = storageLogin as GlobalServicesLoginDetails;
      } else {
        this.onPremLoginDetails = storageLogin as OnPremLoginDetails;
      }

      this.trackAuthTrace('oauth2-service-storage-population-success', {
        isCloud,
        storageUserPresent: !!storageUser,
        storageUserEmailPresent: !!storageUserEmail,
        storageTokenPresent: !!storageToken,
        storageLoginPresent: !!storageLogin,
      });

      return true;
    } catch (error) {
      const reason =
        error instanceof Error && error.name === 'CorruptedDataError'
          ? 'corrupted storage detected'
          : 'storage access failed';

      this.trackAuthException(
        'oauth2-service-storage-population-failed',
        error,
        {
          reason,
        },
      );

      if (reason === 'corrupted storage detected') {
        if (this.hadStoredCredentials()) {
          this.trackAuthException(
            'oauth2-service-storage-corruption-autherror-set',
            new Error('Auth error flag set due to storage corruption'),
          );
          this.setAuthErrorFlag();
        }
        await this.clearAuthenticationState('storage-corrupted');
      }

      return false;
    }
  }

  private areRequiredCredentialsValid(
    storageUser: string | null,
    storageUserEmail: string | null,
    storageLogin: GlobalServicesLoginDetails | OnPremLoginDetails | undefined,
    storageToken: OAuth2Token | undefined,
  ): boolean {
    if (!storageUser || !storageUserEmail || !storageLogin || !storageToken) {
      if (this.hadStoredCredentials()) {
        this.handleMissingCredentials(storageUser, storageUserEmail);
      }
      return false;
    }
    return true;
  }

  recordAuthenticatedUse(): void {
    const now = Date.now();
    this.lastAuthenticatedUseMs = now;

    if (
      this.storagePartitionKey &&
      now - this.lastPersistedUseMs >= this.LAST_USE_WRITE_THROTTLE_MS
    ) {
      this.lastPersistedUseMs = now;
      try {
        localStorage.setItem(
          this.storagePartitionKey + STORAGE_NAME_LAST_USE,
          now.toString(),
        );
        this.lastUsePersistFailureTracked = false;
      } catch (error) {
        if (!this.lastUsePersistFailureTracked) {
          this.lastUsePersistFailureTracked = true;
          this.trackAuthException(
            'oauth2-service-last-use-persist-failed',
            error,
            { throttleMs: this.LAST_USE_WRITE_THROTTLE_MS },
          );
        }
      }
    }
  }

  getLastAuthenticatedUseMs(): number | undefined {
    const storedValue = this.storagePartitionKey
      ? Number(
          localStorage.getItem(
            this.storagePartitionKey + STORAGE_NAME_LAST_USE,
          ),
        )
      : 0;
    const lastUseMs = Math.max(
      this.lastAuthenticatedUseMs,
      Number.isFinite(storedValue) ? storedValue : 0,
    );

    return lastUseMs > 0 ? lastUseMs : undefined;
  }

  isAccessTokenEffectivelyValid(nowMs = Date.now()): boolean {
    return isTokenEffectivelyValid(
      this.oAuth2Token,
      this.getLastAuthenticatedUseMs(),
      nowMs,
    );
  }

  setRefreshInProgress(): void {
    localStorage.setItem(
      this.storagePartitionKey + STORAGE_NAME_REFRESH_IN_PROGRESS,
      Date.now().toString(),
    );
  }

  clearRefreshInProgress(): void {
    localStorage.removeItem(
      this.storagePartitionKey + STORAGE_NAME_REFRESH_IN_PROGRESS,
    );
  }

  setStorageMxUserEmail(email: string): void {
    this.mxUserEmail = email;
    localStorage.setItem(
      this.storagePartitionKey + STORAGE_NAME_USER_EMAIL,
      this.mxUserEmail,
    );
  }

  getStorageMxUserEmail(): string | null {
    const userEmail = localStorage.getItem(
      this.storagePartitionKey + STORAGE_NAME_USER_EMAIL,
    );
    if (!userEmail) return null;
    this.mxUserEmail = userEmail;
    return this.mxUserEmail;
  }

  setStorageMxUserId(userId: string): void {
    this.mxUserId = userId;
    localStorage.setItem(
      this.storagePartitionKey + STORAGE_NAME_USER_ID,
      this.mxUserId,
    );
  }

  getStorageMxUserId(): string | null {
    const userId = localStorage.getItem(
      this.storagePartitionKey + STORAGE_NAME_USER_ID,
    );
    if (!userId) return null;
    this.mxUserId = userId;
    return this.mxUserId;
  }

  setStorageLoginDetails(cloudLoginDetails: GlobalServicesLoginDetails): void {
    this.cloudLoginDetails = cloudLoginDetails;
    localStorage.setItem(
      this.storagePartitionKey + STORAGE_NAME_LOGIN_DETAILS,
      JSON.stringify(cloudLoginDetails),
    );
  }

  getStorageLoginDetails(): GlobalServicesLoginDetails | undefined {
    const cloudLoginDetails = localStorage.getItem(
      this.storagePartitionKey + STORAGE_NAME_LOGIN_DETAILS,
    );

    if (cloudLoginDetails) {
      try {
        this.cloudLoginDetails = JSON.parse(cloudLoginDetails);
        return this.cloudLoginDetails;
      } catch (error) {
        this.trackAuthException(
          'oauth2-service-login-details-parse-failed',
          error,
          { scope: 'cloud' },
        );
        localStorage.removeItem(
          this.storagePartitionKey + STORAGE_NAME_LOGIN_DETAILS,
        );
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  setStorageOnPremLoginDetails(cloudLoginDetails: OnPremLoginDetails): void {
    this.onPremLoginDetails = cloudLoginDetails;
    localStorage.setItem(
      `${this.storagePartitionKey}${STORAGE_NAME_ON_PREM_HOST}`,
      JSON.stringify(cloudLoginDetails),
    );
  }

  getStorageOnPremLoginDetails(): OnPremLoginDetails | undefined {
    const onPremLoginDetails = localStorage.getItem(
      `${this.storagePartitionKey}${STORAGE_NAME_ON_PREM_HOST}`,
    );

    if (onPremLoginDetails) {
      try {
        this.onPremLoginDetails = JSON.parse(onPremLoginDetails);
        return this.onPremLoginDetails;
      } catch (error) {
        this.trackAuthException(
          'oauth2-service-login-details-parse-failed',
          error,
          { scope: 'on-prem' },
        );
        localStorage.removeItem(
          `${this.storagePartitionKey}${STORAGE_NAME_ON_PREM_HOST}`,
        );
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  /**
   * Persist a token.
   * `origin: 'local'` (default) = this context rotated it → broadcast to peers.
   * `origin: 'peer'` = received from a peer → do not re-broadcast (avoids echo loop).
   */
  async setStorageToken(
    auth: OAuth2Token,
    options?: { origin?: 'local' | 'peer' },
  ): Promise<boolean> {
    if (!this.mxUserId || !this.encryptPassword) {
      this.trackAuthException(
        'oauth2-service-token-storage-missing-user-credentials',
        new Error('Missing user credentials'),
      );
      return false;
    }

    try {
      if (!this.isValidTokenStructure(auth)) {
        this.trackAuthException(
          'oauth2-service-token-storage-invalid-structure',
          new Error('Invalid token structure'),
        );
        return false;
      }

      this.trackAuthTrace('oauth2-service-token-storage-secure-write');
      const success = await this.secureStorage.storeCredentials<OAuth2Token>(
        ENHANCED_STORAGE_KEYS.OAUTH2_TOKEN,
        auth,
      );

      if (success) {
        const verifyToken =
          await this.secureStorage.getCredentials<OAuth2Token>(
            ENHANCED_STORAGE_KEYS.OAUTH2_TOKEN,
          );

        if (!verifyToken?.access_token || !verifyToken.refresh_token) {
          this.trackAuthException(
            'oauth2-service-token-storage-verification-failed',
            new Error('Token verification failed after storage'),
            {
              hasToken: !!verifyToken,
              hasAccessToken: !!verifyToken?.access_token,
              hasRefreshToken: !!verifyToken?.refresh_token,
            },
          );
          return false;
        }

        await this.secureStorage.storeCredentials<StoredUserCredentials>(
          ENHANCED_STORAGE_KEYS.USER_CREDENTIALS,
          {
            userId: this.mxUserId,
            userEmail: this.mxUserEmail ?? '',
            partitionKey: this.storagePartitionKey,
            lastLogin: Date.now(),
          },
        );

        const loginDetails: StoredLoginDetails = {
          isCloud: this.getIsCloudFromStorage(),
          cloudDetails: this.cloudLoginDetails,
          onPremDetails: this.onPremLoginDetails,
        };

        await this.secureStorage.storeCredentials<StoredLoginDetails>(
          ENHANCED_STORAGE_KEYS.LOGIN_DETAILS,
          loginDetails,
        );

        this.oAuth2Token = auth;
        this.context.token = auth.access_token ?? '';
        this.context.refresh_token = auth.refresh_token ?? '';

        this.trackAuthTrace('oauth2-service-token-storage-success', {
          tokenHash: hashForTracking(auth.refresh_token),
          verified: true,
        });

        if (options?.origin !== 'peer') {
          try {
            this.peerTokenBridge?.broadcastTokenToPeers(auth);
          } catch (error) {
            this.trackAuthException(
              'oauth2-service-token-storage-delegate-notify-failed',
              error,
            );
          }
        }
      } else {
        this.trackAuthException(
          'oauth2-service-token-storage-secure-storage-false',
          new Error('Secure storage returned false'),
        );
      }

      return success;
    } catch (error) {
      const errorResult = normalizeError(error);
      this.trackAuthException('oauth2-service-token-storage-exception', error, {
        errorResult,
      });
      return false;
    }
  }

  async getStorageToken(options?: {
    skipRefresh?: boolean;
  }): Promise<OAuth2Token | undefined> {
    this.trackAuthTrace('oauth2-service-storage-token-fetch-started', {
      skipRefresh: options?.skipRefresh ?? false,
    });

    try {
      const token = await this.secureStorage.getCredentials<OAuth2Token>(
        ENHANCED_STORAGE_KEYS.OAUTH2_TOKEN,
      );

      if (!token) {
        this.trackAuthTrace('oauth2-service-storage-token-fetch-no-token');
      } else {
        const tokenHash = hashForTracking(token.refresh_token);
        this.trackAuthTrace('oauth2-service-storage-token-fetch-retrieved', {
          tokenHash,
        });
      }

      const corruptionResult = await this.handleTokenCorruption(token);
      if (corruptionResult.isCorrupted) {
        return undefined;
      }

      const candidateToken =
        token ?? (this.oAuth2Token?.refresh_token ? this.oAuth2Token : null);

      if (candidateToken) {
        if (
          candidateToken.expires_at &&
          !(candidateToken.expires_at instanceof Date)
        ) {
          candidateToken.expires_at = new Date(candidateToken.expires_at);
        }
        // Only update in-memory token if it's not expired,
        // or if we're not in a skipRefresh path (which means a refresh is about to happen)
        if (!this.isRawTokenExpired(candidateToken) || !options?.skipRefresh) {
          this.oAuth2Token = candidateToken;
        }
      }

      const tokenExpired = candidateToken
        ? this.isRawTokenExpired(candidateToken)
        : false;
      const resolvedToken = await this.handleTokenExpiration(
        candidateToken,
        options,
      );
      const usedRefresh =
        !!candidateToken && tokenExpired && !(options?.skipRefresh ?? false);

      this.trackAuthTrace('oauth2-service-storage-token-fetch-complete', {
        skipRefresh: options?.skipRefresh ?? false,
        tokenPresent: !!candidateToken,
        tokenExpired,
        returnedTokenPresent: !!resolvedToken,
        usedRefresh,
      });

      return resolvedToken;
    } catch (error) {
      this.handleTokenRetrievalError(error);
      return undefined;
    }
  }

  private async handleTokenCorruption(
    token: OAuth2Token | null,
  ): Promise<{ isCorrupted: boolean }> {
    if (token || !this.hadStoredCredentials()) {
      return { isCorrupted: false };
    }

    const clearedMarker = this.getAuthClearedMarker();
    if (clearedMarker) {
      // Recover only from network-error clears (possible background-tab false positives).
      // Server-confirmed auth losses always kill the session.
      const isRecoverableReason =
        clearedMarker.reason === 'cors-masked-token-expiry';

      if (isRecoverableReason && this.oAuth2Token?.refresh_token) {
        this.trackAuthTrace(
          'oauth2-service-storage-corruption-attempting-recovery',
          {
            hasInMemoryRefreshToken: true,
            clearedReason: clearedMarker.reason,
          },
        );
        return { isCorrupted: false };
      }
      return { isCorrupted: true };
    }

    if (this.oAuth2Token?.refresh_token) {
      this.trackAuthTrace(
        'oauth2-service-storage-read-failed-using-memory-fallback',
        {
          hadStoredCredentials: true,
          hasInMemoryToken: true,
          hasInMemoryRefreshToken: true,
        },
      );
      return { isCorrupted: false };
    }

    if (await this.isRefreshInProgressAndResolvable()) {
      return { isCorrupted: false };
    }

    this.trackAuthException(
      'oauth2-service-storage-corruption-credentials-missing',
      new Error('Stored credentials missing'),
      {
        hadStoredCredentials: true,
        hasInMemoryToken: !!this.oAuth2Token,
      },
    );
    this.setAuthErrorFlag();
    await this.clearAuthenticationState('storage-token-corrupted');
    return { isCorrupted: true };
  }

  private getAuthClearedMarker(): {
    reason: string;
    timestamp: number;
  } | null {
    const authClearedRaw = sessionStorage.getItem(AUTH_CLEARED_MARKER_KEY);
    if (!authClearedRaw) {
      return null;
    }

    const marker = this.parseAuthClearedMarker(authClearedRaw);
    const isRecentClear =
      marker !== null &&
      Date.now() - marker.timestamp < this.AUTH_CLEARED_MARKER_TTL_MS;

    sessionStorage.removeItem(AUTH_CLEARED_MARKER_KEY);

    if (isRecentClear && marker) {
      this.trackAuthTrace(
        'oauth2-service-storage-missing-after-explicit-clear',
        {
          authClearedReason: marker.reason,
          markerAge: Date.now() - marker.timestamp,
        },
      );
      return marker;
    }

    return null;
  }

  private async isRefreshInProgressAndResolvable(): Promise<boolean> {
    const refreshInProgressKey =
      this.storagePartitionKey + STORAGE_NAME_REFRESH_IN_PROGRESS;
    const refreshTimestamp = localStorage.getItem(refreshInProgressKey);
    const refreshStartedAt = this.parseRefreshInProgress(refreshTimestamp);

    if (refreshStartedAt === null) {
      return false;
    }

    const refreshAge = Date.now() - refreshStartedAt;
    if (refreshAge >= this.REFRESH_CORRUPTION_GRACE_WINDOW_MS) {
      return false;
    }

    this.trackAuthTrace(
      'oauth2-service-storage-corruption-deferred-refresh-active',
      { refreshAge, refreshTimestamp },
    );

    await new Promise((resolve) => setTimeout(resolve, 2000));
    const retryToken = await this.secureStorage.getCredentials<OAuth2Token>(
      ENHANCED_STORAGE_KEYS.OAUTH2_TOKEN,
    );

    if (retryToken) {
      this.trackAuthTrace(
        'oauth2-service-storage-corruption-resolved-after-retry',
      );
      return true;
    }

    return false;
  }

  private async handleTokenExpiration(
    token: OAuth2Token | null,
    options?: { skipRefresh?: boolean },
  ): Promise<OAuth2Token | undefined> {
    if (!token || !this.isRawTokenExpired(token)) {
      return token ?? undefined;
    }

    this.trackAuthTrace('oauth2-service-token-expiration-detected', {
      skipRefresh: options?.skipRefresh ?? false,
      isBrowserOnlyRoute: this.isBrowserOnlyRoute,
    });

    if (options?.skipRefresh) {
      this.trackAuthTrace('oauth2-service-token-expiration-refresh-skipped');
      return token;
    }
    if (this.isBrowserOnlyRoute) {
      // Browser-only popup never refreshes (no refresh token); its interceptor signals session-expiry on auth failure. Return undefined so non-interceptor callers don't get an expired token.
      this.trackAuthTrace(
        'oauth2-service-token-expiration-refresh-skipped-browser-only',
      );
      return undefined;
    }

    this.trackAuthTrace('oauth2-service-token-expiration-refresh-triggered');
    const refreshedToken = await firstValueFrom(
      this.refreshToken(undefined, true, 'oauth2-service-token-expiration'),
    );

    if (!refreshedToken) {
      this.trackAuthException(
        'oauth2-service-token-refresh-failed-after-expiration',
        new Error('Token refresh failed during storage retrieval'),
      );
    }

    return refreshedToken ?? undefined;
  }

  private handleTokenRetrievalError(error: unknown): void {
    const errorResult = normalizeError(error);

    const isDecryptionFailure = this.isDecryptionFailure(error);
    const isStorageCorrupted = this.isStorageCorrupted(error);

    this.trackAuthException('oauth2-service-token-retrieval-failed', error, {
      errorResult,
      isDecryptionFailure,
      isStorageCorrupted,
      hasInMemoryToken: !!this.oAuth2Token,
      hasInMemoryRefreshToken: !!this.oAuth2Token?.refresh_token,
    });

    if (isDecryptionFailure || isStorageCorrupted) {
      if (this.oAuth2Token?.refresh_token) {
        this.trackAuthTrace(
          'oauth2-service-token-retrieval-error-using-memory-fallback',
          {
            isDecryptionFailure,
            isStorageCorrupted,
          },
        );
      } else {
        this.handleStorageAuthenticationFailure();
      }
    }
  }

  private handleStorageAuthenticationFailure(): void {
    this.trackAuthException(
      'oauth2-service-storage-authentication-failure',
      new Error('Storage authentication failure'),
      { hadStoredCredentials: this.hadStoredCredentials() },
    );

    if (this.hadStoredCredentials()) {
      this.setAuthErrorFlag();
    }

    this.clearAuthenticationState('storage-authentication-failure');
  }

  async clearAuth(
    reason = 'clear-auth',
    metadata?: Record<string, unknown>,
  ): Promise<boolean> {
    try {
      this.trackKickoutTrace(reason, 'clearAuth', metadata);

      await this.removeSecureCredentials(
        [
          ENHANCED_STORAGE_KEYS.OAUTH2_TOKEN,
          ENHANCED_STORAGE_KEYS.USER_CREDENTIALS,
          ENHANCED_STORAGE_KEYS.LOGIN_DETAILS,
        ],
        'sequential',
      );

      localStorage.removeItem(this.storagePartitionKey + STORAGE_NAME_TOKEN);
      localStorage.removeItem(this.storagePartitionKey + STORAGE_NAME_USER_ID);
      localStorage.removeItem(
        this.storagePartitionKey + STORAGE_NAME_USER_EMAIL,
      );
      localStorage.removeItem(
        this.storagePartitionKey + STORAGE_NAME_LOGIN_DETAILS,
      );
      localStorage.removeItem(
        this.storagePartitionKey + STORAGE_NAME_ON_PREM_HOST,
      );
      localStorage.removeItem(this.storagePartitionKey + STORAGE_NAME_LAST_USE);
      localStorage.removeItem(IS_CLOUD_STORAGE_KEY);

      this.clearRefreshInProgress();
      this.secureStorage.regenerateSessionId();

      this.oAuth2Token = undefined;
      this.mxUserId = undefined;
      this.mxUserEmail = undefined;
      this.cloudLoginDetails = undefined;
      this.onPremLoginDetails = undefined;
      this.lastAuthenticatedUseMs = 0;
      this.lastPersistedUseMs = 0;

      this.restorePartitionKeyFromStorage();

      this.context.clearAuthentication();
      return true;
    } catch (error) {
      this.trackAuthException('oauth2-service-auth-clear-failed', error);
      return false;
    }
  }

  persistPartitionKey(): void {
    if (this.storagePartitionKey) {
      localStorage.setItem(
        STORAGE_KEY_PARTITION,
        obfuscateStorageValue(this.storagePartitionKey),
      );
    }
  }

  getPersistedPartitionKey(): string | undefined {
    const stored = localStorage.getItem(STORAGE_KEY_PARTITION);
    return stored ? deobfuscateStorageValue(stored) : undefined;
  }

  private restorePartitionKeyFromStorage(): void {
    const savedPartitionKey = this.getPersistedPartitionKey();
    if (savedPartitionKey) {
      this.storagePartitionKey = savedPartitionKey;
    }
  }

  hydrateBrowserOnlyAccessToken(
    payload: BrowserOnlyAccessTokenHydration,
  ): boolean {
    if (!payload.accessToken) {
      return false;
    }

    if (payload.isCloud) {
      if (!payload.cloudLoginDetails) {
        return false;
      }
      this.cloudLoginDetails = payload.cloudLoginDetails;
      this.onPremLoginDetails = undefined;
    } else {
      if (!payload.onPremLoginDetails) {
        return false;
      }
      this.onPremLoginDetails = payload.onPremLoginDetails;
      this.cloudLoginDetails = undefined;
    }

    const { expiresAt, expiresIn } = this.resolveBrowserOnlyExpiry(
      payload.expiresAt,
      payload.expiresIn,
    );

    this.mxUserId = payload.mxUserId;
    this.mxUserEmail = payload.mxUserEmail;
    this.oAuth2Token = {
      access_token: payload.accessToken,
      refresh_token: '',
      token_type: payload.tokenType ?? 'Bearer',
      expires_in: expiresIn,
      expires_at: expiresAt,
    };

    this.context.token = payload.accessToken;
    this.context.refresh_token = '';

    const success = payload.isCloud
      ? this.setCloudContext()
      : this.setOnPremContext();

    if (!success) {
      this.trackAuthException(
        'oauth2-service-browser-only-hydration-failed',
        new Error('Failed to hydrate browser-only context'),
        { isCloud: payload.isCloud },
      );
    }

    return success;
  }

  private resolveBrowserOnlyExpiry(
    expiresAt: Date | string | undefined,
    expiresIn: number | undefined,
  ): { expiresAt: Date; expiresIn: number } {
    const parsedAt = expiresAt ? new Date(expiresAt) : null;
    const validAt =
      parsedAt && !Number.isNaN(parsedAt.getTime()) ? parsedAt : null;
    const validIn =
      typeof expiresIn === 'number' && Number.isFinite(expiresIn)
        ? Math.max(expiresIn, 1)
        : null;

    const resolvedAt =
      validAt ??
      new Date(
        Date.now() +
          (validIn ?? DEFAULT_BROWSER_ONLY_EXPIRES_IN) *
            MILLISECONDS_PER_SECOND,
      );
    const resolvedIn =
      validIn ??
      Math.max(
        Math.ceil(
          (resolvedAt.getTime() - Date.now()) / MILLISECONDS_PER_SECOND,
        ),
        1,
      );

    return { expiresAt: resolvedAt, expiresIn: resolvedIn };
  }

  populateContextOAuthData(isCloud = true): boolean {
    if (!this.oAuth2Token) {
      return false;
    }

    localStorage.setItem(IS_CLOUD_STORAGE_KEY, isCloud ? 'true' : 'false');

    this.setCommonTokenContext();
    const success = isCloud ? this.setCloudContext() : this.setOnPremContext();

    if (!success) {
      this.trackAuthException(
        'oauth2-service-populate-context-failed',
        new Error('Failed to populate context'),
        { isCloud },
      );
    }

    return success;
  }

  private setCommonTokenContext(): void {
    this.context.token = this.oAuth2Token?.access_token ?? '';
    this.context.refresh_token = this.oAuth2Token?.refresh_token ?? '';
  }

  private setCloudContext(): boolean {
    if (!this.cloudLoginDetails) {
      return false;
    }

    const firstWorkspace =
      this.cloudLoginDetails.tenant.workspaces[FIRST_WORKSPACE_INDEX];
    this.context.isCloud = true;
    this.context.alias = this.cloudLoginDetails.tenant.alias;
    this.context.tenantId = this.cloudLoginDetails.tenant.id;
    this.context.api = this.buildCloudApiUrl(firstWorkspace.urls.api);
    this.context.website = this.buildCloudWebsiteUrl(firstWorkspace.urls.web);
    return true;
  }

  private setOnPremContext(): boolean {
    if (!this.onPremLoginDetails) {
      return false;
    }

    this.context.isCloud = false;
    this.context.alias = this.onPremLoginDetails.database ?? '';
    this.context.api =
      this.onPremLoginDetails.host + '/MaximizerWebData/Data.svc';
    this.context.website = `${this.onPremLoginDetails.host}/MaximizerWebAccess`;
    return true;
  }

  private buildCloudApiUrl(apiUrl: string | null | undefined): string {
    return apiUrl ? apiUrl + '/Data.svc' : '';
  }

  private buildCloudWebsiteUrl(webUrl: string | null | undefined): string {
    return webUrl ? webUrl.replace(/\/$/, '') : '';
  }

  private getLoginHint(isCloud = true): string | null {
    let database = '';
    if (isCloud) {
      if (!this.cloudLoginDetails) return null;
      if (this.cloudLoginDetails.tenant.workspaces.length === 0) return null;
      database =
        this.cloudLoginDetails.tenant.workspaces[FIRST_WORKSPACE_INDEX]
          .database;
    } else {
      if (!this.onPremLoginDetails) return null;
      database = this.onPremLoginDetails.database ?? '';
    }

    return DATABASE_LOGIN_HINT_PREFIX + database;
  }

  private getOAuthUrl(): string | null {
    if (!this.cloudLoginDetails) return null;
    if (this.cloudLoginDetails.tenant.workspaces.length === 0) return null;
    const oauthUrl =
      this.cloudLoginDetails.tenant.workspaces[FIRST_WORKSPACE_INDEX].urls
        .oauth;
    if (!oauthUrl) return null;
    return oauthUrl;
  }

  private getRegionClientId(oauthUrl: string): string {
    const firstCharacters = oauthUrl.substring(0, URL_PREFIX_CHECK_LENGTH);
    if (
      (firstCharacters.includes(RegionalSubdomain.UK) ||
        firstCharacters.includes(RegionalSubdomain.EMEA)) &&
      this.clientIdEmea
    ) {
      return this.clientIdEmea;
    }
    return this.clientId;
  }

  private isValidTokenStructure(token: OAuth2Token): boolean {
    return !!(
      token &&
      typeof token === 'object' &&
      token.access_token &&
      typeof token.access_token === 'string' &&
      token.token_type &&
      typeof token.expires_in === 'number' &&
      token.expires_in > 0
    );
  }

  private isRawTokenExpired(token: OAuth2Token): boolean {
    if (!token.expires_at) {
      return true;
    }

    const now = new Date();
    const expirationTime = new Date(token.expires_at);
    const bufferTime = this.getExpirationBufferMs(token);
    const effectiveExpiration = expirationTime.getTime() - bufferTime;
    const isExpired = now.getTime() >= effectiveExpiration;

    return isExpired;
  }

  /** Refresh buffer scaled to lifetime: ~20% (min 30s) for short, capped at TOKEN_BUFFER_MS for long. */
  private getExpirationBufferMs(token: OAuth2Token): number {
    const MIN_DYNAMIC_BUFFER_MS = 30_000;
    const DYNAMIC_BUFFER_RATIO = 0.2;

    if (typeof token.expires_in !== 'number' || token.expires_in <= 0) {
      return TOKEN_BUFFER_MS;
    }

    const dynamicBufferMs = Math.floor(
      token.expires_in * MILLISECONDS_PER_SECOND * DYNAMIC_BUFFER_RATIO,
    );
    const boundedBufferMs = Math.max(MIN_DYNAMIC_BUFFER_MS, dynamicBufferMs);

    return Math.min(TOKEN_BUFFER_MS, boundedBufferMs);
  }

  private detectAuthenticationLoss(error: unknown): boolean {
    const authLossStatuses = [
      HTTP_STATUS.UNAUTHORIZED,
      HTTP_STATUS.FORBIDDEN,
    ] as const;

    const authLossMessages = [
      'invalid_grant',
      'invalid_token',
      'invalid_request',
      'token_expired',
      'refresh_token_expired',
      'unauthorized',
      'forbidden',
      'authentication_failed',
      'session_expired',
    ];

    const status = this.getErrorStatus(error);
    if (
      status !== undefined &&
      authLossStatuses.includes(status as 401 | 403)
    ) {
      return true;
    }

    const errorMessage = this.extractAuthErrorMessage(error);
    return authLossMessages.some((msg) => errorMessage.includes(msg));
  }

  private categorizeAuthError(error: unknown): string {
    const status = this.getErrorStatus(error);
    if (status === HTTP_STATUS.UNAUTHORIZED) return 'UNAUTHORIZED';
    if (status === HTTP_STATUS.FORBIDDEN) return 'FORBIDDEN';
    if (status === HTTP_STATUS.BAD_REQUEST) {
      const message = this.extractAuthErrorMessage(error);
      if (
        message.includes('invalid_grant') ||
        message.includes('invalid refresh_token') ||
        message.includes('invalid refresh token') ||
        message.includes('refresh_token_expired')
      ) {
        return 'INVALID_REFRESH_TOKEN';
      }
      if (message.includes('invalid_client')) return 'INVALID_CLIENT';
      return 'BAD_REQUEST';
    }
    if (status === HTTP_STATUS.NOT_FOUND) return 'ENDPOINT_NOT_FOUND';
    if (status !== undefined && status >= HTTP_STATUS.INTERNAL_SERVER_ERROR)
      return 'SERVER_ERROR';
    if (status === HTTP_STATUS.NETWORK_ERROR) {
      if (this.oAuth2Token && this.isRawTokenExpired(this.oAuth2Token)) {
        return 'NETWORK_ERROR_TOKEN_EXPIRED';
      }
      return 'NETWORK_ERROR';
    }
    return 'UNKNOWN_ERROR';
  }

  private async waitForNewerTokenAfterReuse(
    tokenHash: string,
  ): Promise<OAuth2Token | null> {
    for (const delayMs of this.REUSE_WAIT_RETRY_DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));

      if (
        this.hasDifferentRefreshTokenInMemory(tokenHash) &&
        this.oAuth2Token
      ) {
        const adoptedToken = this.adoptTokenState(this.oAuth2Token);
        this.trackAuthTrace(
          'oauth2-service-token-refresh-reuse-resolved-from-memory',
          {
            tokenHash,
            storedTokenHash: hashForTracking(adoptedToken.refresh_token),
            usageCount: this.refreshTokenUsageCount.get(tokenHash) ?? 0,
          },
        );
        return adoptedToken;
      }

      const storedToken = await this.secureStorage.getCredentials<OAuth2Token>(
        ENHANCED_STORAGE_KEYS.OAUTH2_TOKEN,
      );
      if (storedToken?.refresh_token) {
        const storedHash = hashForTracking(storedToken.refresh_token);
        if (storedHash !== tokenHash) {
          if (
            storedToken.expires_at &&
            !(storedToken.expires_at instanceof Date)
          ) {
            storedToken.expires_at = new Date(storedToken.expires_at);
          }

          this.trackAuthTrace(
            'oauth2-service-token-refresh-reuse-resolved-from-storage',
            {
              tokenHash,
              storedTokenHash: storedHash,
              usageCount: this.refreshTokenUsageCount.get(tokenHash) ?? 0,
            },
          );
          return this.adoptTokenState(storedToken);
        }
      }
    }

    this.trackAuthTrace('oauth2-service-token-refresh-reuse-no-newer-token', {
      tokenHash,
      usageCount: this.refreshTokenUsageCount.get(tokenHash) ?? 0,
    });
    return null;
  }

  private hasDifferentRefreshTokenInMemory(requestTokenHash: string): boolean {
    if (!this.oAuth2Token?.refresh_token) return false;
    return hashForTracking(this.oAuth2Token.refresh_token) !== requestTokenHash;
  }

  private adoptTokenState(token: OAuth2Token): OAuth2Token {
    const normalizedToken = this.normalizeTokenExpiration(token);
    this.oAuth2Token = normalizedToken;
    this.context.token = normalizedToken.access_token ?? '';
    this.context.refresh_token = normalizedToken.refresh_token ?? '';
    return normalizedToken;
  }

  private normalizeTokenExpiration(token: OAuth2Token): OAuth2Token {
    if (token.expires_at && !(token.expires_at instanceof Date)) {
      return {
        ...token,
        expires_at: new Date(token.expires_at),
      };
    }

    return token;
  }

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Refresh wait timeout'));
      }, timeoutMs);

      promise
        .then((result) => {
          clearTimeout(timer);
          resolve(result);
        })
        .catch((error) => {
          clearTimeout(timer);
          reject(error instanceof Error ? error : new Error(String(error)));
        });
    });
  }

  private parseRefreshInProgress(value: string | null): number | null {
    if (!value) return null;

    const numeric = Number(value);
    if (!Number.isNaN(numeric) && numeric > 0) {
      return numeric;
    }

    const parsedDate = Date.parse(value);
    return Number.isNaN(parsedDate) ? null : parsedDate;
  }

  private parseAuthClearedMarker(
    value: string,
  ): { reason: string; timestamp: number } | null {
    try {
      const parsed = JSON.parse(value) as {
        reason?: string;
        timestamp?: number;
      };
      if (!parsed.reason || !parsed.timestamp) {
        return null;
      }
      return {
        reason: parsed.reason,
        timestamp: parsed.timestamp,
      };
    } catch {
      return null;
    }
  }

  private extractAuthErrorMessage(error: unknown): string {
    const record = this.asRecord(error);
    const nested = this.asRecord(record?.['error']);
    const messageParts = [
      record?.['message'],
      record?.['error_description'],
      record?.['error'],
      nested?.['error_description'],
      nested?.['message'],
      nested?.['error'],
    ];

    return messageParts
      .filter((part): part is string => typeof part === 'string')
      .join(' ')
      .toLowerCase();
  }

  private setAuthErrorFlag(reason = 'auth'): void {
    sessionStorage.setItem(AUTH_ERROR_SESSION_KEY, reason);
  }

  private async clearAuthenticationState(
    reason = 'unspecified',
  ): Promise<void> {
    if (this.clearingInProgress) {
      return;
    }

    this.clearingInProgress = true;

    try {
      this.trackKickoutTrace(reason, 'clearAuthenticationState');
      this.trackAuthTrace('oauth2-service-authentication-clear-start', {
        reason,
      });
      this.oAuth2Token = undefined;
      this.context.token = '';
      this.context.refresh_token = '';

      sessionStorage.setItem(
        AUTH_CLEARED_MARKER_KEY,
        JSON.stringify({
          reason,
          timestamp: Date.now(),
        }),
      );

      await this.removeSecureCredentials(
        [
          ENHANCED_STORAGE_KEYS.ACCESS_TOKEN,
          ENHANCED_STORAGE_KEYS.REFRESH_TOKEN,
          ENHANCED_STORAGE_KEYS.OAUTH2_TOKEN,
          ENHANCED_STORAGE_KEYS.USER_CREDENTIALS,
          ENHANCED_STORAGE_KEYS.LOGIN_DETAILS,
          ENHANCED_STORAGE_KEYS.TOKEN_METADATA,
        ],
        'parallel',
      );

      if (this.storagePartitionKey) {
        [
          STORAGE_NAME_USER_ID,
          STORAGE_NAME_USER_EMAIL,
          STORAGE_NAME_LOGIN_DETAILS,
          STORAGE_NAME_TOKEN,
          STORAGE_NAME_LAST_USE,
        ].forEach((key) =>
          localStorage.removeItem(this.storagePartitionKey + key),
        );
      }
      this.lastAuthenticatedUseMs = 0;
      this.lastPersistedUseMs = 0;
    } catch (error) {
      const errorResult = normalizeError(error);
      this.trackAuthException(
        'oauth2-service-clear-authentication-state-failed',
        error,
        {
          errorResult,
        },
      );
    } finally {
      this.clearingInProgress = false;
    }
  }

  private trackKickoutTrace(
    reason: string,
    source: 'clearAuth' | 'clearAuthenticationState',
    metadata?: Record<string, unknown>,
  ): void {
    if (reason === 'manual-logout') {
      return;
    }

    if (!this.hadStoredCredentials()) {
      return;
    }

    const locationPath =
      typeof window !== 'undefined' ? window.location.pathname : '';
    const locationSearch =
      typeof window !== 'undefined' ? window.location.search : '';

    this.trackAuthTrace(
      'oauth2-service-user-kicked-out',
      {
        source,
        reason,
        locationPath,
        locationSearch,
        authErrorFlag: sessionStorage.getItem(AUTH_ERROR_SESSION_KEY) ?? '',
        hasAuthClearedMarker: !!sessionStorage.getItem(AUTH_CLEARED_MARKER_KEY),
        clearingInProgress: this.clearingInProgress,
        hasContextToken: !!this.context.token,
        hasContextRefreshToken: !!this.context.refresh_token,
        hasStoragePartitionKey: !!this.storagePartitionKey,
        lastRefreshFailureReason: this.lastRefreshFailureReason ?? '',
        ...metadata,
      },
      SeverityLevel.Warning,
    );
  }

  private async removeSecureCredentials(
    keys: readonly string[],
    mode: 'sequential' | 'parallel' = 'sequential',
  ): Promise<void> {
    if (mode === 'parallel') {
      await Promise.all(
        keys.map((key) => this.secureStorage.removeCredentials(key)),
      );
      return;
    }

    for (const key of keys) {
      await this.secureStorage.removeCredentials(key);
    }
  }

  private isDecryptionFailure(error: unknown): boolean {
    const errorMessage = normalizeError(error).toLowerCase();
    const decryptionErrorIndicators = [
      'utf-8 conversion failed',
      'decryption failed',
      'invalid encrypted data',
      'cipher not supported',
      'malformed',
      'invalid base64',
      'authentication tag verification failed',
      'invalid key',
      'decrypt error',
    ];

    return decryptionErrorIndicators.some((indicator) =>
      errorMessage.includes(indicator),
    );
  }

  private isStorageCorrupted(error: unknown): boolean {
    const errorMessage = normalizeError(error).toLowerCase();
    const corruptionIndicators = [
      'unexpected end of json',
      'invalid json',
      'storage corrupted',
      'data integrity',
      'checksum failed',
      'invalid data format',
      'parse error',
      'malformed data',
    ];

    return corruptionIndicators.some((indicator) =>
      errorMessage.includes(indicator),
    );
  }

  private getErrorStatus(error: unknown): number | undefined {
    const record = this.asRecord(error);
    const status = record?.['status'];
    return typeof status === 'number' ? status : undefined;
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : null;
  }

  private hadStoredCredentials(): boolean {
    const hasInstanceCredentials = !!(
      this.mxUserId ??
      this.mxUserEmail ??
      this.cloudLoginDetails ??
      this.onPremLoginDetails
    );
    const hasStorageCredentials = !!(
      localStorage.getItem(this.storagePartitionKey + STORAGE_NAME_USER_ID) ??
      localStorage.getItem(this.storagePartitionKey + STORAGE_NAME_USER_EMAIL)
    );

    const hasSecureStorageItems =
      this.secureStorage.hasStoredPayload(ENHANCED_STORAGE_KEYS.OAUTH2_TOKEN) ||
      this.secureStorage.hasStoredPayload(
        ENHANCED_STORAGE_KEYS.USER_CREDENTIALS,
      ) ||
      this.secureStorage.hasStoredPayload(ENHANCED_STORAGE_KEYS.LOGIN_DETAILS);

    const result =
      hasInstanceCredentials || hasStorageCredentials || hasSecureStorageItems;

    return result;
  }

  private handleMissingCredentials(
    storageUser: string | null,
    storageUserEmail: string | null,
  ): void {
    const reason =
      this.hadStoredCredentials() && (!storageUser || !storageUserEmail)
        ? 'credentials corrupted'
        : 'missing required data';

    this.trackAuthException(
      'oauth2-service-storage-population-missing-credentials',
      new Error('Storage population failed'),
      { reason },
    );

    if (reason === 'credentials corrupted') {
      this.setAuthErrorFlag();
    }
  }

  private trackAuthException(
    event: string,
    error: unknown,
    metadata?: Record<string, unknown>,
  ): void {
    const insights = this.getInsightsService();
    if (!insights) return;

    const exception =
      error instanceof Error ? error : new Error(normalizeError(error));
    const errorMessage = normalizeError(exception);
    insights.trackException(
      { error: exception },
      this.withBaseTelemetry({ event, errorMessage, ...metadata }),
    );
  }

  private trackAuthTrace(
    event: string,
    metadata?: Record<string, unknown>,
    severityLevel: SeverityLevel = SeverityLevel.Verbose,
  ): void {
    const insights = this.getInsightsService();
    if (!insights) return;

    insights.trackTrace(
      { message: event, severityLevel },
      this.withBaseTelemetry({
        ...metadata,
        level: SeverityLevel[severityLevel],
      }),
    );
  }

  private withBaseTelemetry(
    metadata?: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      component: 'OAuth2Service',
      hasOAuthToken: !!this.oAuth2Token,
      hasRefreshToken: !!this.oAuth2Token?.refresh_token,
      hasEmail: !!this.mxUserEmail,
      isCloudStored: this.getIsCloudFromStorageWithFallback(),
      ...metadata,
    };
  }

  private validateOAuth2Params(params: {
    code?: string;
    state?: string;
    redirect_uri?: string;
    client_id?: string;
  }): {
    valid: boolean;
    sanitized: {
      code?: string;
      state?: string;
      redirect_uri?: string;
      client_id?: string;
    };
    errors: string[];
  } {
    // Accepts RFC 3986 unreserved + base64url/base64 chars (MS codes contain ~+/=).
    const tokenPattern = /^[A-Za-z0-9\-._~+/=]+$/;
    const maxLength = 2048;
    const fields = [
      {
        key: 'code',
        type: 'token',
        error: 'Invalid authorization code format',
      },
      { key: 'state', type: 'token', error: 'Invalid state parameter format' },
      {
        key: 'redirect_uri',
        type: 'url',
        error: 'Invalid redirect URI format',
      },
      { key: 'client_id', type: 'token', error: 'Invalid client ID format' },
    ] as const;

    const sanitized: {
      code?: string;
      state?: string;
      redirect_uri?: string;
      client_id?: string;
    } = {};
    const errors: string[] = [];

    for (const { key, type, error } of fields) {
      const raw = params[key];
      if (!raw) continue;
      // Validate the same (trimmed) value we store, so validation and sanitized can't diverge.
      const value = raw.trim();
      const isValid =
        type === 'url'
          ? this.isValidUrl(value)
          : tokenPattern.test(value) && value.length <= maxLength;
      if (isValid) {
        sanitized[key] = value;
      } else {
        errors.push(error);
      }
    }

    return { valid: errors.length === 0, sanitized, errors };
  }

  private isValidUrl(value: string): boolean {
    try {
      const url = new URL(value);
      if (url.protocol === 'https:') return true;
      if (url.protocol === 'http:') {
        return url.hostname === 'localhost' || url.hostname === '127.0.0.1';
      }
      return false;
    } catch {
      return false;
    }
  }
}
